/** @format */

/**
 * Stripe Fee Calculator for Pass-Through Pricing (Frontend)
 *
 * Calculates pass-through Stripe processing fees so that the user pays enough
 * to cover Stripe's fee while the full net amount is still collected.
 *
 * All calculations are currency-aware to correctly handle:
 *  • Different regional percentage + fixed-fee rates
 *  • Zero-decimal currencies (JPY, KRW, etc.) where "cents" = whole units
 */

import { ZERO_DECIMAL_CURRENCIES, THREE_DECIMAL_CURRENCIES } from "./currencyUtils";

/**
 * Per-currency Stripe fee configuration (percentage + fixed fee in smallest unit).
 * Mirrors the server-side STRIPE_FEE_CONFIG table.
 */
const STRIPE_FEE_CONFIG: Record<string, { percentage: number; fixedSmallestUnit: number }> = {
	// ── North America ───────────────────────────────────────────────────────
	usd: { percentage: 0.029, fixedSmallestUnit: 30 },
	cad: { percentage: 0.029, fixedSmallestUnit: 30 },
	mxn: { percentage: 0.036, fixedSmallestUnit: 300 },

	// ── Europe ──────────────────────────────────────────────────────────────
	eur: { percentage: 0.015, fixedSmallestUnit: 25 },
	gbp: { percentage: 0.015, fixedSmallestUnit: 20 },
	chf: { percentage: 0.029, fixedSmallestUnit: 30 },
	sek: { percentage: 0.014, fixedSmallestUnit: 180 },
	nok: { percentage: 0.024, fixedSmallestUnit: 200 },
	dkk: { percentage: 0.014, fixedSmallestUnit: 180 },
	pln: { percentage: 0.015, fixedSmallestUnit: 80 },
	czk: { percentage: 0.029, fixedSmallestUnit: 650 },
	huf: { percentage: 0.029, fixedSmallestUnit: 8500 },
	ron: { percentage: 0.029, fixedSmallestUnit: 120 },
	bgn: { percentage: 0.029, fixedSmallestUnit: 50 },
	isk: { percentage: 0.014, fixedSmallestUnit: 4100 },
	all: { percentage: 0.029, fixedSmallestUnit: 2700 },
	bam: { percentage: 0.029, fixedSmallestUnit: 55 },
	mdl: { percentage: 0.029, fixedSmallestUnit: 530 },
	mkd: { percentage: 0.029, fixedSmallestUnit: 1700 },
	rsd: { percentage: 0.029, fixedSmallestUnit: 3300 },
	amd: { percentage: 0.029, fixedSmallestUnit: 12000 },
	try: { percentage: 0.029, fixedSmallestUnit: 1000 },
	ils: { percentage: 0.029, fixedSmallestUnit: 110 },

	// ── Asia-Pacific ────────────────────────────────────────────────────────
	aud: { percentage: 0.0175, fixedSmallestUnit: 30 },
	nzd: { percentage: 0.029, fixedSmallestUnit: 30 },
	sgd: { percentage: 0.034, fixedSmallestUnit: 50 },
	hkd: { percentage: 0.034, fixedSmallestUnit: 240 },
	jpy: { percentage: 0.036, fixedSmallestUnit: 35 },
	krw: { percentage: 0.036, fixedSmallestUnit: 350 },
	inr: { percentage: 0.029, fixedSmallestUnit: 2500 },
	myr: { percentage: 0.030, fixedSmallestUnit: 150 },
	thb: { percentage: 0.0365, fixedSmallestUnit: 1000 },
	idr: { percentage: 0.029, fixedSmallestUnit: 490000 },
	php: { percentage: 0.035, fixedSmallestUnit: 1500 },
	vnd: { percentage: 0.029, fixedSmallestUnit: 7400 },
	twd: { percentage: 0.029, fixedSmallestUnit: 1000 },
	khr: { percentage: 0.029, fixedSmallestUnit: 120000 },
	lkr: { percentage: 0.029, fixedSmallestUnit: 9000 },
	pkr: { percentage: 0.029, fixedSmallestUnit: 8400 },
	mnt: { percentage: 0.029, fixedSmallestUnit: 102000 },
	mop: { percentage: 0.034, fixedSmallestUnit: 242 },
	bnd: { percentage: 0.034, fixedSmallestUnit: 40 },
	uzs: { percentage: 0.029, fixedSmallestUnit: 380000 },

	// ── Middle East ─────────────────────────────────────────────────────────
	aed: { percentage: 0.029, fixedSmallestUnit: 110 },
	sar: { percentage: 0.029, fixedSmallestUnit: 112 },
	qar: { percentage: 0.029, fixedSmallestUnit: 109 },
	// Three-decimal currencies (× 1000)
	kwd: { percentage: 0.029, fixedSmallestUnit: 92 },
	omr: { percentage: 0.029, fixedSmallestUnit: 116 },
	jod: { percentage: 0.029, fixedSmallestUnit: 213 },
	bhd: { percentage: 0.029, fixedSmallestUnit: 113 },
	tnd: { percentage: 0.029, fixedSmallestUnit: 930 },

	// ── Latin America ───────────────────────────────────────────────────────
	brl: { percentage: 0.039, fixedSmallestUnit: 60 },
	ars: { percentage: 0.036, fixedSmallestUnit: 33000 },
	clp: { percentage: 0.029, fixedSmallestUnit: 270 },
	cop: { percentage: 0.029, fixedSmallestUnit: 120000 },
	pen: { percentage: 0.029, fixedSmallestUnit: 112 },
	uyu: { percentage: 0.039, fixedSmallestUnit: 1200 },
	bob: { percentage: 0.029, fixedSmallestUnit: 207 },
	crc: { percentage: 0.039, fixedSmallestUnit: 16000 },
	dop: { percentage: 0.039, fixedSmallestUnit: 1800 },
	gtq: { percentage: 0.039, fixedSmallestUnit: 235 },
	gyd: { percentage: 0.039, fixedSmallestUnit: 6300 },
	pyg: { percentage: 0.029, fixedSmallestUnit: 2200 },

	// ── Caribbean ───────────────────────────────────────────────────────────
	ttd: { percentage: 0.039, fixedSmallestUnit: 204 },
	jmd: { percentage: 0.039, fixedSmallestUnit: 4600 },
	xcd: { percentage: 0.029, fixedSmallestUnit: 81 },
	bsd: { percentage: 0.029, fixedSmallestUnit: 30 },

	// ── Africa ──────────────────────────────────────────────────────────────
	zar: { percentage: 0.029, fixedSmallestUnit: 550 },
	ngn: { percentage: 0.039, fixedSmallestUnit: 48000 },
	ghs: { percentage: 0.039, fixedSmallestUnit: 460 },
	kes: { percentage: 0.039, fixedSmallestUnit: 3800 },
	xof: { percentage: 0.039, fixedSmallestUnit: 180 },
	rwf: { percentage: 0.039, fixedSmallestUnit: 350 },
	egp: { percentage: 0.029, fixedSmallestUnit: 1470 },
	mad: { percentage: 0.029, fixedSmallestUnit: 300 },
	tzs: { percentage: 0.029, fixedSmallestUnit: 77000 },
	etb: { percentage: 0.039, fixedSmallestUnit: 1700 },
	bwp: { percentage: 0.029, fixedSmallestUnit: 400 },
	gmd: { percentage: 0.039, fixedSmallestUnit: 2100 },
	mga: { percentage: 0.039, fixedSmallestUnit: 1380 },
	mur: { percentage: 0.039, fixedSmallestUnit: 1400 },
	nad: { percentage: 0.029, fixedSmallestUnit: 550 },
};

const getStripeFeeConfig = (currency: string) =>
	STRIPE_FEE_CONFIG[currency.toLowerCase()] ?? STRIPE_FEE_CONFIG.usd;

/**
 * Return a human-readable fee rate string, e.g. "2.9% + $0.30" for USD.
 */
export const getFeeSummaryLabel = (currency: string = "usd"): string => {
	const cur = currency.toLowerCase();
	const { percentage, fixedSmallestUnit } = getStripeFeeConfig(cur);
	const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has(cur);
	const isThreeDecimal = THREE_DECIMAL_CURRENCIES.has(cur);
	let fixedDisplay: number;
	if (isZeroDecimal) {
		fixedDisplay = fixedSmallestUnit;
	} else if (isThreeDecimal) {
		fixedDisplay = fixedSmallestUnit / 1000;
	} else {
		fixedDisplay = fixedSmallestUnit / 100;
	}
	const pct = `${(percentage * 100).toFixed(1).replace(/\.0$/, "")}%`;
	const fixed = isZeroDecimal ? fixedDisplay.toFixed(0) : isThreeDecimal ? fixedDisplay.toFixed(3) : fixedDisplay.toFixed(2);
	return `${pct} + ${fixed}`;
};

/**
 * Convert display amount to Stripe's smallest currency unit.
 * Zero-decimal: return as-is. Three-decimal: × 1000. Standard: × 100.
 */
export const dollarsToCents = (amount: number, currency: string = "usd"): number => {
	const cur = currency.toLowerCase();
	if (ZERO_DECIMAL_CURRENCIES.has(cur)) return Math.round(amount);
	if (THREE_DECIMAL_CURRENCIES.has(cur)) return Math.round(amount * 1000);
	return Math.round(amount * 100);
};

/**
 * Convert from Stripe's smallest currency unit to display amount.
 * Zero-decimal: return as-is. Three-decimal: ÷ 1000. Standard: ÷ 100.
 */
export const centsToDollars = (amountCents: number, currency: string = "usd"): number => {
	const cur = currency.toLowerCase();
	if (ZERO_DECIMAL_CURRENCIES.has(cur)) return amountCents;
	if (THREE_DECIMAL_CURRENCIES.has(cur)) return amountCents / 1000;
	return amountCents / 100;
};

/**
 * Format amount for display using Intl.NumberFormat.
 */
export const formatCurrency = (amount: number, currency: string = "usd"): string => {
	const cur = currency.toLowerCase();
	const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has(cur);
	const isThreeDecimal = THREE_DECIMAL_CURRENCIES.has(cur);
	const digits = isZeroDecimal ? 0 : isThreeDecimal ? 3 : 2;
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: currency.toUpperCase(),
		minimumFractionDigits: digits,
		maximumFractionDigits: digits,
	}).format(amount);
};

/**
 * Calculate the gross charge (in smallest units) needed so that after Stripe
 * deducts its fee the net amount is still fully collected.
 *
 * gross = ceil( (net + fixed) / (1 - percentage) )
 */
export const calculateGrossChargeFromNetCents = (
	netAmountCents: number,
	currency: string = "usd",
): number => {
	if (!Number.isInteger(netAmountCents) || netAmountCents < 0) {
		throw new Error("netAmountCents must be a non-negative integer");
	}
	const { percentage, fixedSmallestUnit } = getStripeFeeConfig(currency);
	return Math.ceil((netAmountCents + fixedSmallestUnit) / (1 - percentage));
};

/**
 * Calculate Stripe processing fee in smallest units from a net owed amount (in smallest units).
 */
export const calculateStripeFeeInCents = (
	netAmountCents: number,
	currency: string = "usd",
): number => {
	return calculateGrossChargeFromNetCents(netAmountCents, currency) - netAmountCents;
};

/**
 * Calculate the total charge from a net owed display amount (e.g. dollars).
 *
 * @param netAmountDollars - Net owed amount in display units (dollars, pounds, etc.)
 * @param currency         - ISO 4217 currency code (default: "usd")
 */
export const calculateTotalCharge = (
	netAmountDollars: number,
	currency: string = "usd",
): {
	netAmount: number;
	stripeFee: number;
	totalCharge: number;
	netAmountCents: number;
	stripeFeeCents: number;
	totalChargeCents: number;
} => {
	const netAmountCents = dollarsToCents(netAmountDollars, currency);
	const totalChargeCents = calculateGrossChargeFromNetCents(netAmountCents, currency);
	const stripeFeeCents = totalChargeCents - netAmountCents;

	return {
		netAmount: centsToDollars(netAmountCents, currency),
		stripeFee: centsToDollars(stripeFeeCents, currency),
		totalCharge: centsToDollars(totalChargeCents, currency),
		netAmountCents,
		stripeFeeCents,
		totalChargeCents,
	};
};

/**
 * Get fee breakdown for display in UI.
 *
 * @param netAmountDollars - Full owed amount before Stripe fees (in display units)
 * @param currency         - ISO 4217 currency code (default: "usd")
 */
export const getFeeBreakdownForDisplay = (
	netAmountDollars: number,
	currency: string = "usd",
): {
	netAmount: string;
	fee: string;
	total: string;
	feePercentageText: string;
	transparencyNote: string;
} => {
	const { netAmount, stripeFee, totalCharge } = calculateTotalCharge(netAmountDollars, currency);
	const feeLabel = getFeeSummaryLabel(currency);

	return {
		netAmount: formatCurrency(netAmount, currency),
		fee: formatCurrency(stripeFee, currency),
		total: formatCurrency(totalCharge, currency),
		feePercentageText: feeLabel,
		transparencyNote: `Your full ${formatCurrency(netAmount, currency)} owed amount is collected in full. The ${formatCurrency(stripeFee, currency)} processing fee covers card payment costs.`,
	};
};
